import {Component, computed, effect, input, OnInit, output, signal} from '@angular/core';
import {IlotDto} from '../../shared/dto/ilot.dto';
import {DimensionModel} from '../model/dimension.model';
import {isBlank} from 'sigale-angular';
import {Ilot} from '../model/ilot.model';
import {Bureau} from '../model/bureau.model';
import {Duree} from '../../shared/models/duree.model';
import {UtilisateurSigalett} from '../../shared/models/utilisateur-sigalett.model';

@Component({
	selector: 'app-grid-ilots',
	templateUrl: './grid-ilots.component.html',
	styleUrl: './grid-ilots.component.scss'
})
export class GridIlotsComponent implements OnInit {

	readonly TAILLE_INDICATEUR_REPERE_PLAN = 40;

	ilots = input.required<Ilot[]>();
	estAffichagePlusieursIlots = input.required<boolean>();
	readonly = input.required<boolean>();
	longueurParent = input.required<number>();
	hauteurParent = input.required<number>();
	bureauIdCourant = input<number>();
	duree = input<Duree>();
	labelHaut = input<string>('');
	labelDroite = input<string>('');
	labelBas = input<string>('');
	labelGauche = input<string>('');
	estAffichageVertical = input.required<boolean>();
	estAfficherLabelIlot = input.required<boolean>();
	bureauPreferentielId = input<number | null>(null);
	utilisateurConnecte = input<UtilisateurSigalett>();

	onSelectionIlot = output<number>();
	onClickBureau = output<Bureau>();

	etatHoverParIlotOuBureauId = signal<Map<number, boolean>>(new Map<number, boolean>);

	tailleBureau = signal<number>(0);

	valeurGridColumns = signal<string>('');
	valeurGridRows = signal<string>('');
	numeroGridColumnBureauParBureauId = signal(new Map<number, number>);
	numeroGridRowBureauParBureauId = signal(new Map<number, number>);

	numeroGridColumnLabelIlotParIlotId = signal(new Map<number, string>);
	numeroGridRowLabelIlotParIlotId = signal(new Map<number, string>);
	valeurLeftLabelIlot = signal(new Map<number, string>);
	valeurTopLabelIlot = signal(new Map<number, string>);

	borderBottomLeftRadiusParBureauId = signal(new Map<number, string>);
	borderBottomRightRadiusParBureauId = signal(new Map<number, string>);
	borderTopLeftRadiusParBureauId = signal(new Map<number, string>);
	borderTopRightRadiusParBureauId = signal(new Map<number, string>);

	tailleGap = computed(() => !this.estAfficherLabelIlot() ? 20 : 50);

	constructor() {
		effect(() => {
			const ilots = this.ilots();

			const etatHoverParIlotOuBureauId = new Map<number, boolean>();

			if (this.estAffichagePlusieursIlots) {
				ilots.forEach(ilot => {
					etatHoverParIlotOuBureauId.set(ilot.id, false);
				});
			}
			else {
				const etatHoverParIlotId = new Map<number, boolean>();
				ilots.flatMap(ilot => ilot.bureaux).forEach(bureau => {
					etatHoverParIlotId.set(bureau.id, false);
				});
			}

			this.etatHoverParIlotOuBureauId.set(etatHoverParIlotOuBureauId);

			this.determinerTailleBureau();
			this.initialiserGrid();

		}, {allowSignalWrites: true});
	}

	ngOnInit(): void {
		// Il faut déterminer la taille avant la création des éléments HTML.
		this.determinerTailleBureau();
	}

	/**
	 * Créé le plan avec le ou les ilots et les bureaux.
	 */
	initialiserGrid(): void {
		const bureaux = this.ilots().flatMap(ilot => ilot.bureaux);
		const dimensionsZoneDeTravail = this.recupererDimensionsGrid(bureaux);

		// Dimension de la grid
		this.valeurGridColumns.set(`repeat(${dimensionsZoneDeTravail.longueur}, ${this.tailleBureau()}px)`);
		this.valeurGridRows.set(`repeat(${dimensionsZoneDeTravail.largeur}, ${this.tailleBureau()}px)`);

		const numeroGridColumnParBureauId = new Map<number, number>;
		const numeroGridRowParBureauId = new Map<number, number>;

		// Dimension des bureaux
		bureaux.forEach(bureau => {
			const numeroGridColumn = bureau.coordonneeX - dimensionsZoneDeTravail.minX + 1;
			const numeroGridRow = bureau.coordonneeY - dimensionsZoneDeTravail.minY + 1;

			numeroGridColumnParBureauId.set(bureau.id, numeroGridColumn);
			numeroGridRowParBureauId.set(bureau.id, numeroGridRow);
		});

		this.numeroGridColumnBureauParBureauId.set(numeroGridColumnParBureauId);
		this.numeroGridRowBureauParBureauId.set(numeroGridRowParBureauId);

		// Border radius des ilots
		this.determinerLeBorderRadiusDesBureaux(bureaux);

		// Positionnement des labels des ilots
		const numeroGridColumnLabelIlotParIlotId = new Map<number, string>;
		const numeroGridRowLabelIlotParIlotId = new Map<number, string>;
		const valeurLeftLabelIlot = new Map<number, string>;
		const valeurTopLabelIlot = new Map<number, string>;

		this.ilots().forEach(ilot => {
			const dimensionsIlot = this.recupererDimensionsGrid(ilot.bureaux);

			const startColumn = dimensionsIlot.minX - dimensionsZoneDeTravail.minX + 1;
			const endColumn = dimensionsIlot.maxX - dimensionsZoneDeTravail.minX + 2; // +2 car grid-column-end est exclusif
			const startRow = dimensionsIlot.minY - dimensionsZoneDeTravail.minY + 1;
			const endRow = dimensionsIlot.maxY - dimensionsZoneDeTravail.minY + 2; // +2 car grid-row-end est exclusif

			// Un caractère fait environ 8.5px. On multiplie donc le nombre de caractères et on ajoute le padding pour avoir la bonne taille du label.
			const tailleLabel = ilot.nom.length * 8.5 + 10;

			numeroGridColumnLabelIlotParIlotId.set(ilot.id, `${startColumn} / ${endColumn}`);
			numeroGridRowLabelIlotParIlotId.set(ilot.id, `${startRow} / ${endRow}`);
			valeurLeftLabelIlot.set(ilot.id, `${(endColumn - startColumn) * this.tailleBureau() / 2 - (tailleLabel / 2)}px`);
			if (this.estAffichageVertical()) {
				valeurTopLabelIlot.set(ilot.id, `${-25}px`);
			}
			else {
				valeurTopLabelIlot.set(ilot.id, `${-40}px`);
			}
		});

		this.numeroGridColumnLabelIlotParIlotId.set(numeroGridColumnLabelIlotParIlotId);
		this.numeroGridRowLabelIlotParIlotId.set(numeroGridRowLabelIlotParIlotId);
		this.valeurLeftLabelIlot.set(valeurLeftLabelIlot);
		this.valeurTopLabelIlot.set(valeurTopLabelIlot);
	}

	/**
	 * Détermine la dimension qu'auront les bureaux. Pour ce faire, on se base sur la taille de l'écran afin de s'assurer que les ilots seront affichés correctement.
	 */
	determinerTailleBureau(): void {
		const gapVertical = this.tailleGap() * 2; // Gap haut + Gap bas
		const gapHorizontal = this.tailleGap() * 2; // Gap gauche + Gap droite
		const tailleLabelBordureVerticalOuHorizontal = this.TAILLE_INDICATEUR_REPERE_PLAN * 2; // taille indicateur haut / gauche + taille indicateur bas / droite

		const longueurPlanSansIndicateurEtGap = this.longueurParent() - tailleLabelBordureVerticalOuHorizontal - gapHorizontal;
		const hauteurPlanSansIndicateurEtGap = this.hauteurParent() - tailleLabelBordureVerticalOuHorizontal - gapVertical;

		const bureaux = this.ilots().flatMap(ilot => ilot.bureaux);

		const coordonneesX = bureaux.map(bureau => bureau.coordonneeX);
		const coordonneesY = bureaux.map(bureau => bureau.coordonneeY);

		const minX = Math.min(...coordonneesX);
		const maxX = Math.max(...coordonneesX);
		const minY = Math.min(...coordonneesY);
		const maxY = Math.max(...coordonneesY);

		const longueurPlanIlots = maxX - minX + 1;
		const hauteurPlanIlots = maxY - minY + 1;

		const tailleBureauEnFonctionLongueurPlanEnPx = Math.floor((longueurPlanSansIndicateurEtGap) / (longueurPlanIlots));
		const tailleBureauEnFonctionLargeurPlanEnPx = Math.floor((hauteurPlanSansIndicateurEtGap) / (hauteurPlanIlots));

		// On fixe la taille des bureaux en prenant la plus petite valeur pour être sur que le plan ne dépasse pas de la taille du composant parent.
		const tailleBureau = tailleBureauEnFonctionLongueurPlanEnPx < tailleBureauEnFonctionLargeurPlanEnPx ? tailleBureauEnFonctionLongueurPlanEnPx : tailleBureauEnFonctionLargeurPlanEnPx;

		this.tailleBureau.set(tailleBureau);
	}

	/**
	 * Détermine pour chaque bureau quel angle aura un bord rond. Pour se faire on regarde pour chaque bureau s'il possède des voisins.
	 *
	 * @param bureaux les bureaux.
	 */
	determinerLeBorderRadiusDesBureaux(bureaux: Bureau[]): void {
		const bureauParCoordonneeXY = new Map<string, Bureau>();

		bureaux.forEach(bureau => {
			bureauParCoordonneeXY.set(`${bureau.coordonneeX};${bureau.coordonneeY}`, bureau);
		});

		const voisinHaut = {x: 0, y: -1};
		const voisinBas = {x: 0, y: +1};
		const voisinGauche = {x: -1, y: 0};
		const voisinDroite = {x: +1, y: 0};

		const borderBottomLeftRadiusParBureauId = new Map<number, string>;
		const borderBottomRightRadiusParBureauId = new Map<number, string>;
		const borderTopLeftRadiusParBureauId = new Map<number, string>;
		const borderTopRightRadiusParBureauId = new Map<number, string>;

		bureaux.forEach(bureau => {
			const coordonneesVoisinHaut = {x: bureau.coordonneeX + voisinHaut.x, y: bureau.coordonneeY + voisinHaut.y};
			const coordonneesVoisinBas = {x: bureau.coordonneeX + voisinBas.x, y: bureau.coordonneeY + voisinBas.y};
			const coordonneesVoisinGauche = {x: bureau.coordonneeX + voisinGauche.x, y: bureau.coordonneeY + voisinGauche.y};
			const coordonneesVoisinDroite = {x: bureau.coordonneeX + voisinDroite.x, y: bureau.coordonneeY + voisinDroite.y};

			const borderRadius = '20px';

			borderBottomLeftRadiusParBureauId.set(bureau.id, borderRadius);
			borderBottomRightRadiusParBureauId.set(bureau.id, borderRadius);
			borderTopLeftRadiusParBureauId.set(bureau.id, borderRadius);
			borderTopRightRadiusParBureauId.set(bureau.id, borderRadius);

			const borderRadiusNone = '0px';

			if (this.possedeBureauPourCoordonnees(bureauParCoordonneeXY, coordonneesVoisinHaut.x, coordonneesVoisinHaut.y)) {
				borderTopRightRadiusParBureauId.set(bureau.id, borderRadiusNone);
				borderTopLeftRadiusParBureauId.set(bureau.id, borderRadiusNone);
			}

			if (this.possedeBureauPourCoordonnees(bureauParCoordonneeXY, coordonneesVoisinDroite.x, coordonneesVoisinDroite.y)) {
				borderTopRightRadiusParBureauId.set(bureau.id, borderRadiusNone);
				borderBottomRightRadiusParBureauId.set(bureau.id, borderRadiusNone);
			}

			if (this.possedeBureauPourCoordonnees(bureauParCoordonneeXY, coordonneesVoisinBas.x, coordonneesVoisinBas.y)) {
				borderBottomLeftRadiusParBureauId.set(bureau.id, borderRadiusNone);
				borderBottomRightRadiusParBureauId.set(bureau.id, borderRadiusNone);
			}

			if (this.possedeBureauPourCoordonnees(bureauParCoordonneeXY, coordonneesVoisinGauche.x, coordonneesVoisinGauche.y)) {
				borderTopLeftRadiusParBureauId.set(bureau.id, borderRadiusNone);
				borderBottomLeftRadiusParBureauId.set(bureau.id, borderRadiusNone);
			}
		});

		this.borderTopRightRadiusParBureauId.set(borderTopRightRadiusParBureauId);
		this.borderBottomRightRadiusParBureauId.set(borderBottomRightRadiusParBureauId);
		this.borderBottomLeftRadiusParBureauId.set(borderBottomLeftRadiusParBureauId);
		this.borderTopLeftRadiusParBureauId.set(borderTopLeftRadiusParBureauId);
	}

	/**
	 * Vérifie s'il y a un Bureau aux coordonnées renseignées.
	 *
	 * @param bureauParCoordonneeXY map de bureau par coordonnées. La clé de la map est du type x;y.
	 * @param x la coordonnée x.
	 * @param y la coordonnée y.
	 * @private
	 */
	private possedeBureauPourCoordonnees(bureauParCoordonneeXY: Map<string, Bureau>, x: number, y: number) {
		const cle = `${x};${y}`;
		return bureauParCoordonneeXY.has(cle);
	}

	/**
	 * Récupère les dimensions du plan en fonction de tous les bureaux.
	 *
	 * @param bureaux les bureaux.
	 */
	recupererDimensionsGrid(bureaux: Bureau[]): DimensionModel {
		const coordonneesX: number[] = [];
		const coordonneesY: number[] = [];

		bureaux.forEach(bureau => {
			coordonneesX.push(bureau.coordonneeX);
			coordonneesY.push(bureau.coordonneeY);
		});

		const minX = Math.min(...coordonneesX);
		const maxX = Math.max(...coordonneesX);
		const minY = Math.min(...coordonneesY);
		const maxY = Math.max(...coordonneesY);

		// Le +1 permet de prendre en compte la première case.
		// Ex : l'abscisse de ma zone de travail fait 12 cases alors minX = 0 et maxX = 11. 11 - 0 = 11. Il me manque une case.
		const longueur = maxX - minX + 1;
		const largeur = maxY - minY + 1;

		return <DimensionModel>{
			minX: minX,
			maxX: maxX,
			minY: minY,
			maxY: maxY,
			longueur: longueur,
			largeur: largeur
		};
	}

	/**
	 * Emet une notification avec l'id de l'ilot et le bureau sélectionne si celui n'est pas réservé. Le parent n'a plus qu'a écouté la notification qu'il souhaite
	 * selon s'il désire réagir au clique sur l'ilot ou sur le bureau.
	 *
	 * @param ilot l'ilot sur lequel l'utilisateur a cliqué.
	 * @param bureau le bureau sur lequel l'utilisateur a cliqué.
	 */
	onClickIlotOuBureau(ilot: IlotDto, bureau: Bureau): void {
		this.onSelectionIlot.emit(ilot.id);
		if (this.estBureauReservable(bureau)) {
			this.onClickBureau.emit(bureau);
		}
	}

	/**
	 * Permet de gérer le hover sur un ilot ou sur un bureau. On met à jour le hover si l'affichage possède plusieurs ilots ou que le bureau soit réservable
	 *
	 * @param ilotId l'id de l'ilot.
	 * @param bureau le bureau concerné.
	 * @param nouvelEtat le nouvel état du hover. True si l'utilisateur à la souris au-dessus, False sinon.
	 */
	changerEtatHover(ilotId: number, bureau: Bureau, nouvelEtat: boolean): void {
		if (this.estAffichagePlusieursIlots() || this.estBureauReservable(bureau)) {
			this.etatHoverParIlotOuBureauId.update(etatHoverParIlotOuBureau => {
				if (this.estAffichagePlusieursIlots()) {
					return new Map(etatHoverParIlotOuBureau).set(ilotId, nouvelEtat);
				}

				return new Map(etatHoverParIlotOuBureau).set(bureau.id, nouvelEtat);
			});
		}
	}

	estBureauReservable(bureau: Bureau): boolean {
		return !this.readonly() && (!bureau.estReserveDansLaJournee() ||
			(bureau.reserverSeulementMatin() && (this.duree() === Duree.APRES_MIDI || this.duree() === Duree.JOURNEE)) ||
			(bureau.reserverSeulementApresMidi() && (this.duree() === Duree.MATIN || this.duree() === Duree.JOURNEE)));
	}

	/**
	 * True si l'ilot est survolé et qu'on affiche plusieurs ilots.
	 *
	 * @param ilot l'ilot concerné.
	 */
	determineClasseHoveredPourIlot(ilot: IlotDto): boolean {
		return this.estAffichagePlusieursIlots() && this.etatHoverParIlotOuBureauId().get(ilot.id);
	}

	/**
	 * True si le bureau est survolé qu'on affiche qu'un seul ilot et que le bureau est réservable.
	 *
	 * @param bureau le bureau concerné.
	 */
	determinerClasseHoveredIndicateurBureau(bureau: Bureau): boolean {
		return !this.estAffichagePlusieursIlots() && this.estBureauReservable(bureau) && this.etatHoverParIlotOuBureauId().get(bureau.id);
	}

	recupererNomPrenomOccupant(bureau: Bureau): string {
		return this.estAffichagePlusieursIlots() ? bureau.prenomsReservant() : bureau.prenomsNomsReservant();
	}

	protected readonly isBlank = isBlank;
}
